import { clsx } from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  useMobileMediaQuery,
  useTabletMediaQuery,
} from '../../hooks/mediaQueries';
import { useWindowListener } from '../../hooks/useWindowListener';
import { fakeButtonProps, handleEnterKey } from '../../utils/handleEnterKey';
import { marginCSSVars } from '../../utils/spacing';
import type { PopoverProps } from './popover.types';
import { PopoverContent } from './popoverContent/popoverContent';
import { PopoverContext } from './popoverContext/popoverContext';
import { PopoverDivider } from './popoverDivider/popoverDivider';
import { useHandleMobilePopover } from './useHandleMobilePopover';

import styles from './popover.module.css';

/**
 * Popover is used to display content on top of another, used for dropdown menus or for filtering.
 */

function BasePopover({
  alignment = { horizontal: 'left', vertical: 'bottom' },
  children,
  className,
  closeOnTargetClick = true,
  contentRole,
  'data-testid': testId,
  disableMobileStyling = false,
  id,
  isLayered = false,
  label,
  matchTargetWidth = false,
  noMinWidth,
  onClose,
  onOpen,
  openOn = 'click',
  role = 'combobox',
  style,
  target,
  targetWidth = 'fit-content',
  variant = 'action',
  ...marginProps
}: PopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<HTMLSpanElement>(null);
  const contentRef = useRef<HTMLDivElement & HTMLDialogElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const enterPressedOnTargetRef = useRef(false);
  const isTablet = useTabletMediaQuery({});
  const isMobile = useMobileMediaQuery({});
  const hasMobileStyling = isMobile && !disableMobileStyling;
  const isModal = hasMobileStyling && contentRole === 'dialog';
  const ContentElement = isModal ? 'dialog' : 'div';

  const closePopover = useCallback(() => {
    const content = contentRef.current;
    // Only reclaim focus that would be lost when the content unmounts, so
    // clicking away onto another element keeps focus there.
    const shouldRestoreFocus = content?.contains(document.activeElement);
    // The rest of the page stays inert, and unfocusable, until a modal
    // dialog closes.
    if (content?.open) content.close();
    if (shouldRestoreFocus) {
      const restoreTarget = restoreFocusRef.current?.isConnected
        ? restoreFocusRef.current
        : targetRef.current;
      restoreTarget?.focus();
    }
    setIsOpen(false);
    onClose && onClose();
  }, [onClose]);

  const openPopover = useCallback(() => {
    const activeElement = document.activeElement;
    restoreFocusRef.current =
      activeElement instanceof HTMLElement &&
      targetRef.current?.contains(activeElement)
        ? activeElement
        : targetRef.current;
    setIsOpen(true);
    onOpen && onOpen();
  }, [onOpen]);

  const handleClick = () => {
    if (openOn !== 'disabled') {
      if (isOpen && closeOnTargetClick) closePopover();
      if (!isOpen) openPopover();
    }
  };

  const handleMouseEnter = useCallback(() => {
    if (openOn === 'hover') {
      openPopover();
    }
  }, [openOn, openPopover]);

  const handleMouseLeave = useCallback(() => {
    if (openOn === 'hover') {
      closePopover();
    }
  }, [openOn, closePopover]);

  /** close when user clicks outside of popover */
  const handleClickAway = (event: MouseEvent) => {
    const domPath = event.composedPath && event.composedPath();

    const eventTarget = domPath ? domPath[0] : (event.target as HTMLElement);

    const popoverContainsTarget = popoverRef.current?.contains(
      eventTarget as Node
    );

    if (!popoverContainsTarget && isOpen) {
      closePopover();
    }
  };

  /** allows keyboard users to close the popover using esc key  */
  const handleEscapeKey = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (isOpen) {
          closePopover();
        }
      }
      return;
    },
    [closePopover, isOpen]
  );

  /** openOn changed to 'click' for mobile screens */
  const openOnHandler = () => {
    if (openOn === 'hover' && !isTablet) {
      /** @todo adjust for hook immutability */
      // eslint-disable-next-line react-hooks/immutability
      openOn = 'click';
    }
    return;
  };

  /** different states of children depending how PopoverContext is used */
  const renderedChildren =
    typeof children === 'function'
      ? children({ closePopover, isOpen })
      : children;

  openOnHandler();

  useEffect(() => {
    if (!isOpen || contentRole !== 'dialog' || openOn === 'hover') return;

    if (isModal && !contentRef.current?.open) {
      contentRef.current?.showModal();
    }
    contentRef.current?.focus();
  }, [isOpen, isModal, contentRole, openOn]);

  useWindowListener('keydown', handleEscapeKey);

  useWindowListener('click', handleClickAway);

  /**
   * on mobile, it resizes iframe when popover is open
   * allowing the drawer to appear at the bottom of the screen
   */
  useHandleMobilePopover(isOpen, hasMobileStyling, isLayered);

  return (
    <PopoverContext.Provider value={{ closePopover, isOpen }}>
      {isOpen && !disableMobileStyling && !isModal && (
        <div
          className={styles['popover_mobileOverlay']}
          style={{
            '--position': isMobile
              ? disableMobileStyling
                ? 'relative'
                : 'fixed'
              : 'static',
          }}
        />
      )}

      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className={clsx(className, styles['popover_container'])}
        id={id}
        style={{
          '--width': targetWidth,
          ...marginCSSVars(marginProps),
          ...style,
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && isOpen) {
            // Keeps a modal dialog from also firing its native cancel event.
            event.preventDefault();
            event.stopPropagation();
            closePopover();
          }
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        ref={popoverRef}
      >
        <span
          className={styles['popover_target']}
          aria-controls="popover-content"
          aria-expanded={isOpen}
          aria-label={label}
          aria-haspopup={contentRole}
          data-testid={testId}
          ref={targetRef}
          role={role}
          onClick={handleClick}
          onKeyDown={(event) => {
            enterPressedOnTargetRef.current = event.key === 'Enter';
          }}
          onKeyUp={(event) => {
            // Enter on an option closes the popover and restores focus here on
            // keydown, so its keyup must not reopen it.
            if (!enterPressedOnTargetRef.current) return;
            enterPressedOnTargetRef.current = false;
            handleEnterKey(event, openPopover);
          }}
          tabIndex={0}
          {...(closeOnTargetClick && { ...fakeButtonProps })}
        >
          {target}
        </span>
        {isOpen && (
          <div
            className={clsx(styles['popover_alignment'], {
              [styles['popover_alignment__action']]: variant === 'action',
              [styles['popover_alignment__info']]: variant === 'info',
              [styles['popover_alignment__top']]: alignment.vertical === 'top',
              [styles['popover_alignment__left']]:
                alignment.horizontal === 'left',
              [styles['popover_alignment__right']]:
                alignment.horizontal === 'right',
              [styles['popover_alignment__center']]:
                alignment.horizontal === 'center',
              [styles['popover_alignment__noMinWidth']]: noMinWidth,
              [styles['popover_alignment__matchTargetWidth']]: matchTargetWidth,
            })}
          >
            <ContentElement
              className={clsx(styles['popover_content'], {
                [styles['popover_content__mobile']]: !disableMobileStyling,
                [styles['popover_content__alignment_top']]:
                  alignment.vertical === 'top',
                [styles['popover_content__alignment_bottom']]:
                  alignment.vertical === 'bottom',
              })}
              id="popover-content"
              ref={contentRef}
              {...(contentRole === 'dialog' && {
                'aria-label': label,
                role: isModal ? undefined : 'dialog',
                tabIndex: -1,
              })}
              onCancel={(event) => {
                event.preventDefault();
                closePopover();
              }}
              onClick={(event) => {
                // A click on the modal dialog itself rather than its content
                // lands on the backdrop.
                if (isModal && event.target === event.currentTarget) {
                  closePopover();
                }
                // Prevent clicks within popover content from triggering the
                // "Click away" behavior that closes popovers.
                event.stopPropagation();
              }}
              onKeyDown={(event) => {
                // Reefer Modal scopes Tab to its own content, which a layered
                // modal popover makes inert.
                if (isModal && event.key === 'Tab') event.stopPropagation();
              }}
            >
              {renderedChildren}
            </ContentElement>
          </div>
        )}
      </div>
    </PopoverContext.Provider>
  );
}

export const Popover = Object.assign(BasePopover, {
  Content: PopoverContent,
  Divider: PopoverDivider,
});
